import React from 'react';
import { useDarkMode } from '../../context/DarkModeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { PerformancePhase } from './PerformanceReviewDashboard';

interface PerformanceTimelineProps {
  focus: any;
  currentPhase: PerformancePhase;
  monthsElapsed: number;
  totalMonths: number;
}

const phases: Array<{
  id: PerformancePhase;
  label: string;
  icon: string;
  months: string;
  description: string;
}> = [
  { 
    id: 'planning', 
    label: 'Planning', 
    icon: '📋', 
    months: '1-2',
    description: 'Set goals and define metrics'
  },
  { 
    id: 'foundation', 
    label: 'Foundation', 
    icon: '🏗️', 
    months: '3-4',
    description: 'Build momentum and establish routines'
  },
  { 
    id: 'execution', 
    label: 'Execution', 
    icon: '⚡', 
    months: '5-8',
    description: 'Deliver on key initiatives and track progress against your annual goals'
  },
  { 
    id: 'impact', 
    label: 'Impact', 
    icon: '🎯', 
    months: '9-10',
    description: 'Demonstrate outcomes and prepare achievements'
  },
  { 
    id: 'review_prep', 
    label: 'Review Prep', 
    icon: '📝', 
    months: '11-12',
    description: 'Compile evidence and prepare for review'
  }
];

const PerformanceTimeline: React.FC<PerformanceTimelineProps> = ({
  focus,
  currentPhase,
  monthsElapsed,
  totalMonths
}) => {
  const { darkMode } = useDarkMode();
  
  const currentPhaseIndex = phases.findIndex(p => p.id === currentPhase);
  const progressPercentage = (monthsElapsed / totalMonths) * 100;
  
  // Calculate phase-specific progress
  const phaseStartMonth = phases.slice(0, currentPhaseIndex).reduce((acc, phase) => {
    const range = phase.months.split('-').map(Number);
    return acc + (range[1] - range[0] + 1);
  }, 0) + 1;
  
  const currentPhaseData = phases[currentPhaseIndex];
  
  // Format dates
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };
  
  const startDate = new Date(focus.start_date || focus.created_at);
  const reviewDate = new Date(focus.end_date || focus.target_date);
  const today = new Date();
  
  const bgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const progressBg = darkMode ? 'bg-gray-700' : 'bg-gray-200';
  const phaseTextColor = darkMode ? 'text-gray-300' : 'text-gray-600';
  const currentPhaseBg = darkMode ? 'bg-blue-900/30' : 'bg-blue-50';
  const currentPhaseTextColor = darkMode ? 'text-blue-400' : 'text-blue-700';


  return (
    <div className={`${bgColor} ${borderColor} border rounded-lg shadow-sm p-6`}>
      <h2 className={`text-lg font-semibold ${textColor} mb-4`}>Performance Review Timeline</h2>
      
      {/* Progress Overview */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className={`text-sm font-medium ${textColor}`}>
            Phase {currentPhaseIndex + 1} of {phases.length}
          </span>
          <span className={`text-sm font-medium ${textColor}`}>
            {Math.round(progressPercentage)}% Complete
          </span>
        </div>
        
        {/* Progress Bar with Phase Markers */}
        <div className="relative">
          <div className={`h-3 ${progressBg} rounded-full overflow-hidden`}>
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500" 
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          
          {/* Phase Markers */}
          <div className="absolute inset-0 flex justify-between items-center px-1">
            {phases.map((phase, index) => {
              const isCompleted = index < currentPhaseIndex;
              const isCurrent = index === currentPhaseIndex;
              const markerPosition = (index / (phases.length - 1)) * 100;
              
              return (
                <div
                  key={phase.id}
                  className="absolute"
                  style={{ left: `${markerPosition}%`, transform: 'translateX(-50%)' }}
                >
                  <div className={`w-4 h-4 rounded-full border-2 border-white shadow-sm flex items-center justify-center ${
                    isCompleted ? 'bg-blue-600' : isCurrent ? 'bg-blue-600 animate-pulse' : 'bg-gray-300'
                  }`}>
                    {isCompleted && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                    {isCurrent && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      {/* Phase Labels */}
      <div className="flex justify-between text-xs mb-4">
        {phases.map((phase, index) => {
          const isCurrent = phase.id === currentPhase;
          return (
            <span 
              key={phase.id} 
              className={`font-medium ${
                isCurrent ? currentPhaseTextColor : phaseTextColor
              }`}
            >
              {phase.icon} {phase.label}
            </span>
          );
        })}
      </div>
      
      {/* Dates */}
      <div className={`flex justify-between items-center text-sm ${mutedTextColor} mb-4`}>
        <div>
          <span className="font-medium">Started:</span> {formatDate(startDate)}
        </div>
        <div>
          <span className="font-medium">Today:</span> {today.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
        </div>
        <div>
          <span className="font-medium">Review:</span> {formatDate(reviewDate)}
        </div>
      </div>
      
      {/* Current Phase Info */}
      <div className={`p-3 ${currentPhaseBg} rounded-lg`}>
        <div className="flex items-center">
          <span className="text-lg mr-2">{currentPhaseData.icon}</span>
          <div>
            <span className={`font-medium ${darkMode ? 'text-blue-300' : 'text-blue-900'}`}>
              Current Phase: {currentPhaseData.label}
            </span>
            <p className={`text-sm ${currentPhaseTextColor} mt-1`}>
              {currentPhaseData.description}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceTimeline;